import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound, Forbidden } from "@/pages/ErrorPages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/lib/money";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function AccountBilling() {
  const { slug, sp } = useStore();
  const [params] = useSearchParams();
  const justPaid = params.get("just_paid") === "1";
  const { user, status, logout } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [sub, setSub] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const refresh = async () => {
    try {
      const [s, t] = await Promise.all([api.get("/me/subscription"), api.get("/me/transactions")]);
      setSub(s.data.subscription);
      setTxns(t.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    api.get(`/public/tenants/${slug}`)
      .then((r) => { setTenant(r.data); setPageStatus("ready"); })
      .catch(() => setPageStatus("not_found"));
  }, [slug]);

  useEffect(() => {
    if (status === "authenticated") refresh();
  }, [status]);

  if (status === "loading" || pageStatus === "loading") return null;
  if (pageStatus === "not_found" || !tenant) return <NotFound />;
  if (status !== "authenticated" || !user) return <Navigate to={sp(`/login`)} replace />;
  if (user.role === "subscriber" && user.tenant_id !== tenant.id) return <Forbidden />;

  const onCancel = async () => {
    if (!window.confirm("Cancel auto-renew? You'll keep access until the current period ends.")) return;
    setCancelling(true);
    try {
      await api.post("/me/subscription/cancel");
      toast.success("Auto-renew cancelled. You keep access until the period ends.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCancelling(false);
    }
  };

  const subActive = sub && sub.status === "active";
  const subOnAutorenew = subActive && sub.auto_renew;

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav>
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="account-billing-page">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Account · Billing</p>
        <h1 className="mt-2 font-display font-bold text-4xl tracking-tighter">Subscription &amp; payments</h1>

        {justPaid && (
          <div className="mt-6 border border-emerald-500/30 bg-emerald-500/10 p-4 inline-flex items-center gap-3" data-testid="account-billing-success-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
            <span className="text-emerald-100 text-sm">Payment succeeded. Your access is now live.</span>
          </div>
        )}

        <div className="mt-10 bg-[#121212] border border-white/10 p-6" data-testid="account-billing-subscription">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Subscription</div>
          {sub ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-2xl tracking-tight">{sub.plan?.name || "Plan"}</div>
                  <div className="text-white/55 text-sm mt-1">{sub.plan?.description}</div>
                </div>
                <Badge className={`rounded-none uppercase text-[10px] tracking-[0.2em] ${subActive ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/60"}`}>
                  {sub.status}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <Stat label="Amount" value={sub.plan ? formatMoney(sub.plan.price_amount, sub.plan.currency) : "—"} />
                <Stat label={subOnAutorenew ? "Renews on" : "Ends on"} value={formatDate(sub.current_period_end)} testid="account-billing-period-end" />
                <Stat label="Provider" value={sub.provider} />
              </div>
              {subActive && subOnAutorenew && (
                <Button
                  onClick={onCancel}
                  disabled={cancelling}
                  className="mt-6 rounded-none bg-transparent border border-red-500/40 text-red-300 hover:bg-red-500/10 uppercase text-[11px] tracking-[0.25em]"
                  data-testid="account-billing-cancel"
                >
                  {cancelling ? "Cancelling…" : "Cancel auto-renew"}
                </Button>
              )}
              {subActive && !subOnAutorenew && sub.cancelled_at && (
                <div className="mt-6 text-sm text-amber-300/80 inline-flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Cancelled on {formatDate(sub.cancelled_at)}. Your access continues until {formatDate(sub.current_period_end)}.
                </div>
              )}
            </>
          ) : (
            <div className="text-white/55">
              <p>No active subscription. Pick a plan to unlock the full {tenant.name} catalogue.</p>
              <Link
                to={sp(`/plans`)}
                className="mt-4 inline-block px-5 py-2.5 text-xs uppercase tracking-[0.25em]"
                style={{ backgroundColor: tenant.theme_color, color: "#0a0a0a" }}
                data-testid="account-billing-see-plans"
              >
                See plans
              </Link>
            </div>
          )}
        </div>

        <div className="mt-8 bg-[#121212] border border-white/10" data-testid="account-billing-transactions">
          <div className="px-6 pt-5 text-[10px] uppercase tracking-[0.3em] text-white/40">Transaction history</div>
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Kind</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Provider</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {txns.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-white/40 py-8 text-xs uppercase tracking-widest">No transactions yet</TableCell></TableRow>
              ) : (
                txns.map((t) => (
                  <TableRow key={t.id} className="border-white/10 hover:bg-white/5" data-testid={`txn-row-${t.id}`}>
                    <TableCell className="text-xs text-white/60">{formatDate(t.created_at)}</TableCell>
                    <TableCell className="text-sm">{t.kind.replace("_", " ")}</TableCell>
                    <TableCell className="text-xs text-white/60 uppercase tracking-widest">{t.provider}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-none uppercase text-[10px] tracking-[0.2em] ${
                        t.status === "succeeded" ? "bg-emerald-500/15 text-emerald-300" :
                        t.status === "failed" ? "bg-red-500/15 text-red-300" :
                        "bg-amber-500/15 text-amber-300"
                      }`}>{t.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm">{formatMoney(t.amount, t.currency)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </StorefrontLayout>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-black/40 p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.3em] text-white/40">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
